/**
 * TimeBankTab Component
 *
 * Time bank testing panel for triggering automated jobs like accrual,
 * rollover, comp time processing, notifications, and backups.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
    FiClock,
    FiCalendar,
    FiInfo,
    FiDatabase
} from 'react-icons/fi';

const TimeBankTab = ({
    apiRequest,
    onShowNotification,
    onRefreshNotifications
}) => {
    const runMonthlyAccrual = async () => {
        try {
            onShowNotification('Running monthly accrual...', 'success');
            const response = await apiRequest('/api/cron/time-banks/accrual', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({})
            });

            if (response.ok) {
                const data = await response.json();
                const userCount = data.results?.total_users_processed || 0;
                onShowNotification(
                    `Accrual complete! Processed ${userCount} users. Job ID: ${data.job_id}`,
                    'success'
                );
                console.log('Accrual results:', data);
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to run accrual');
            }
        } catch (err) {
            onShowNotification('Accrual failed: ' + err.message, 'error');
            console.error('Accrual error:', err);
        }
    };

    const runYearEndRollover = async () => {
        if (!window.confirm('Run year-end rollover? This will reset comp time to zero for all users.')) {
            return;
        }

        try {
            onShowNotification('Running year-end rollover...', 'success');
            const response = await apiRequest('/api/cron/time-banks/rollover', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({})
            });

            if (response.ok) {
                const data = await response.json();
                const userCount = data.results?.total_users_processed || 0;
                onShowNotification(
                    `Rollover complete! Processed ${userCount} users. Job ID: ${data.job_id}`,
                    'success'
                );
                console.log('Rollover results:', data);
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to run rollover');
            }
        } catch (err) {
            onShowNotification('Rollover failed: ' + err.message, 'error');
            console.error('Rollover error:', err);
        }
    };

    const processCompTime = async () => {
        try {
            onShowNotification('Processing comp time...', 'success');
            const response = await apiRequest('/api/cron/time-banks/comp-time', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({})
            });

            if (response.ok) {
                const data = await response.json();
                const userCount = data.results?.total_users_processed || 0;
                onShowNotification(
                    `Comp time processing complete! Processed ${userCount} users. Job ID: ${data.job_id}`,
                    'success'
                );
                console.log('Comp time results:', data);
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to process comp time');
            }
        } catch (err) {
            onShowNotification('Comp time processing failed: ' + err.message, 'error');
            console.error('Comp time error:', err);
        }
    };

    const testNotifications = async () => {
        try {
            onShowNotification('Creating test notification...', 'success');
            const response = await apiRequest('/api/time-banks/test-notifications', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    notification_type: 'accrual'
                })
            });

            if (response.ok) {
                const data = await response.json();
                onShowNotification(
                    'Test notification created! Check your notification center.',
                    'success'
                );
                console.log('Notification created:', data);
                onRefreshNotifications();
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to create notification');
            }
        } catch (err) {
            onShowNotification('Failed to create notification: ' + err.message, 'error');
            console.error('Notification error:', err);
        }
    };

    const triggerBackup = async () => {
        if (!window.confirm('Run automated backup? This may take a few minutes.')) {
            return;
        }

        try {
            onShowNotification('Starting backup...', 'success');
            const response = await apiRequest('/api/backup/automated', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    backup_type: 'all',
                    include_metadata: true
                })
            });

            if (response.ok) {
                const data = await response.json();
                const summary = data.summary || {};
                onShowNotification(
                    `Backup complete! ${summary.successful_backups || 0}/${summary.total_backups || 0} successful. Job ID: ${data.backup_job_id || 'N/A'}`,
                    'success'
                );
                console.log('Backup results:', data);
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to run backup');
            }
        } catch (err) {
            onShowNotification('Backup failed: ' + err.message, 'error');
            console.error('Backup error:', err);
        }
    };

    return (
        <div className="dos-settings-section">
            <div className="dos-section-header">
                <FiClock className="dos-section-icon" />
                <span className="dos-section-title">Time Bank Testing</span>
            </div>

            <div className="dos-token-testing-grid">
                {/* Run Monthly Accrual */}
                <div className="dos-test-card">
                    <div className="dos-test-card-content">
                        <h4 className="dos-test-title">Monthly Accrual</h4>
                        <p className="dos-test-description">
                            Run monthly accrual for all eligible users (sick & vacation)
                        </p>
                    </div>
                    <button
                        className="dos-test-button dos-primary"
                        onClick={runMonthlyAccrual}
                    >
                        <FiClock style={{ marginRight: '6px' }} />
                        Run Accrual
                    </button>
                </div>

                {/* Run Year-End Rollover */}
                <div className="dos-test-card">
                    <div className="dos-test-card-content">
                        <h4 className="dos-test-title">Year-End Rollover</h4>
                        <p className="dos-test-description">
                            Roll over sick/vacation, reset comp time to zero
                        </p>
                    </div>
                    <button
                        className="dos-test-button dos-warning"
                        onClick={runYearEndRollover}
                    >
                        <FiCalendar style={{ marginRight: '6px' }} />
                        Run Rollover
                    </button>
                </div>

                {/* Run Comp Time Processing */}
                <div className="dos-test-card">
                    <div className="dos-test-card-content">
                        <h4 className="dos-test-title">Process Comp Time</h4>
                        <p className="dos-test-description">
                            Calculate overtime from previous week and accrue comp time
                        </p>
                    </div>
                    <button
                        className="dos-test-button dos-primary"
                        onClick={processCompTime}
                    >
                        <FiClock style={{ marginRight: '6px' }} />
                        Process Comp Time
                    </button>
                </div>

                {/* Test Notifications */}
                <div className="dos-test-card">
                    <div className="dos-test-card-content">
                        <h4 className="dos-test-title">Test Notifications</h4>
                        <p className="dos-test-description">
                            Trigger time bank notification to test notification center
                        </p>
                    </div>
                    <button
                        className="dos-test-button dos-secondary"
                        onClick={testNotifications}
                    >
                        <FiInfo style={{ marginRight: '6px' }} />
                        Send Test Notification
                    </button>
                </div>

                {/* Trigger Backup */}
                <div className="dos-test-card">
                    <div className="dos-test-card-content">
                        <h4 className="dos-test-title">Trigger Backup</h4>
                        <p className="dos-test-description">
                            Run automated backup for all databases (main + orgs)
                        </p>
                    </div>
                    <button
                        className="dos-test-button dos-primary"
                        onClick={triggerBackup}
                    >
                        <FiDatabase style={{ marginRight: '6px' }} />
                        Run Backup
                    </button>
                </div>

                {/* Test Info */}
                <div className="dos-test-card dos-test-card-full">
                    <div className="dos-test-card-content">
                        <h4 className="dos-test-title">Test Info</h4>
                        <p className="dos-test-description">
                            <strong>Monthly Accrual:</strong> Adds sick/vacation hours based on role (Staff: 5.78 sick, 6.67 vacation | Staff Manager: 5.78 sick, 10 vacation). Creates notifications for users.<br/><br/>
                            <strong>Process Comp Time:</strong> Calculates overtime from previous week's time entries (hours over 40/week) and automatically accrues comp time. Caps at 80 hours maximum.<br/><br/>
                            <strong>Year-End Rollover:</strong> Resets comp time to 0, carries over sick/vacation balances. Creates notifications for all affected users.<br/><br/>
                            <strong>Test Notifications:</strong> Sends an accrual notification to your notification center for testing.<br/><br/>
                            <strong>Trigger Backup:</strong> Runs the automated backup job which backs up main + all org databases to S3, cleans up old activities (60+ days) and expired sessions.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

TimeBankTab.propTypes = {
    apiRequest: PropTypes.func.isRequired,
    onShowNotification: PropTypes.func.isRequired,
    onRefreshNotifications: PropTypes.func.isRequired
};

export default TimeBankTab;
